import React, { useEffect, useState, useContext } from "react";
import { 
  Tabs, 
  Tab, 
  Box, 
  LinearProgress, 
  Typography,
  useTheme,
  Chip
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import DashboardContext from "../context/DashboardContext";

const EarningTable = () => {
  const theme = useTheme();
  const [pendingData, setPendingData] = useState([]);
  const [settledData, setSettledData] = useState([]);
  const [currentTab, setCurrentTab] = useState("pending");
  const [loading, setLoading] = useState(false);
  const { sessionUser } = useContext(DashboardContext);

  const columns = [
    { 
      name: "EarningID", 
      label: "EARNING ID",
      options: { 
        setCellHeaderProps: () => ({
          style: { 
            fontWeight: 600, 
            backgroundColor: '#f8fafc',
            fontSize: "0.75rem",
            color: '#64748b',
            padding: '10px 16px',
          },
        }),
      },
    },
    { 
      name: "PVType", 
      label: "EARNING TYPE",
      options: { 
        setCellHeaderProps: () => ({
          style: { 
            fontWeight: 600, 
            backgroundColor: '#f8fafc',
            fontSize: "0.75rem",
            color: '#64748b',
            padding: '10px 16px',
          },
        }),
      },
    },
    { 
      name: "PVValue", 
      label: "PV VALUE",
      options: { 
        setCellHeaderProps: () => ({
          style: { 
            fontWeight: 600, 
            backgroundColor: '#f8fafc',
            fontSize: "0.75rem",
            color: '#64748b',
            padding: '10px 16px',
          },
        }),
        customBodyRender: (value) => (
          <span style={{ fontFamily: 'Roboto Mono', color: '#1e293b' }}>
            {parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </span>
        )
      },
    },
    { 
      name: "PVRate", 
      label: "PV RATE (Rs)",
      options: { 
        setCellHeaderProps: () => ({
          style: { 
            fontWeight: 600, 
            backgroundColor: '#f8fafc',
            fontSize: "0.75rem",
            color: '#64748b',
            padding: '10px 16px',
          },
        }),
        customBodyRender: (value) => (
          <span style={{ color: '#3b82f6' }}>
            {parseFloat(value).toFixed(2)}
          </span>
        )
      },
    },
    { 
      name: "TotalEarning", 
      label: "TOTAL EARNING",
      options: { 
        setCellHeaderProps: () => ({
          style: { 
            fontWeight: 600, 
            backgroundColor: '#f8fafc',
            fontSize: "0.75rem",
            color: '#64748b',
            padding: '10px 16px',
          },
        }),
        customBodyRender: (value) => (
          <span style={{ fontWeight: 600, color: '#16a34a' }}>
            {parseFloat(value).toFixed(2)}
          </span>
        )
      },
    },
    {
      name: "Status",
      label: "STATUS",
      options: {
        setCellHeaderProps: () => ({
          style: { 
            fontWeight: 600, 
            backgroundColor: '#f8fafc',
            fontSize: "0.75rem",
            color: '#64748b',
            padding: '10px 16px',
          },
        }),
        customBodyRender: () => (
          <Chip
            label={currentTab === "pending" ? "Pending" : "Settled"}
            color={currentTab === "pending" ? "warning" : "success"}
            size="small"
            sx={{ 
              fontWeight: 500,
              borderRadius: 1,
              textTransform: 'uppercase',
              fontSize: '0.6875rem'
            }}
          />
        )
      }
    }
  ];

  const options = {
    filterType: "none",
    selectableRows: "none",
    search: true,
    download: false,
    print: false,
    viewColumns: false,
    responsive: 'standard',
    elevation: 2,
    rowsPerPage: 10,
    rowsPerPageOptions: [10, 25, 50],
    searchPlaceholder: 'Search earnings...',
    textLabels: {
      body: {
        noMatch: 'No earnings records found',
      }
    },
    setRowProps: (row, dataIndex, rowIndex) => ({
      style: { 
        cursor: 'pointer', 
        backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc' 
      }
    }),
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const requestBody = {
          ComID: "EH25",
          UserID: sessionUser.UserID,
          Flag: currentTab === "pending" ? "PE" : "SE",
          PVType: "-1",
          ReferenceID: sessionUser.Username,
          FromDate: "2024-06-01",
          ToDate: new Date().toISOString().split('T')[0], // Current date
          AuthCode: sessionUser.AuthCode,
        };

        const response = await axios.post(
          "https://testing.esnep.com/magnessa/api/vendor/earning",
          requestBody,
          { headers: { "Content-Type": "application/json" } }
        );

        if (response.data?.ProdLst) {
          if (currentTab === "pending") {
            setPendingData(response.data.ProdLst);
          } else {
            setSettledData(response.data.ProdLst);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentTab, sessionUser]);

  return (
    <Box sx={{ 
      backgroundColor: 'white', 
      borderRadius: 4,
      boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
      overflow: 'hidden'
    }}>
      <Tabs
        value={currentTab}
        onChange={(_, newValue) => setCurrentTab(newValue)}
        sx={{ 
          px: 3,
          '& .MuiTabs-indicator': {
            height: 4,
            borderRadius: '2px 2px 0 0',
          }
        }}
      >
        <Tab 
          label="Pending Earnings" 
          value="pending" 
          sx={{
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: currentTab === 'pending' ? theme.palette.primary.main : '#64748b',
            py: 2.5,
          }}
        />
        <Tab 
          label="Settled Earnings" 
          value="settled"
          sx={{
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: currentTab === 'settled' ? theme.palette.primary.main : '#64748b',
            py: 2.5,
          }}
        />
      </Tabs>

      {loading && <LinearProgress sx={{ height: 2 }} />}

      <Box sx={{ position: 'relative', minHeight: 200 }}>
        <MUIDataTable
          title={
            <Typography variant="h6" sx={{ 
              px: 1,
              py: 0,
              color: '#1e293b',
              fontSize: '1.125rem',
              fontWeight: 600
            }}>
              {currentTab === 'pending' ? 'Pending Earnings' : 'Settled Earnings'}
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 400, mt: 0.5 }}>
                {currentTab === 'pending' 
                  ? `Showing ${pendingData.length} pending earnings`
                  : `Showing ${settledData.length} settled earnings`}
              </Typography>
            </Typography>
          }
          data={currentTab === "pending" ? pendingData : settledData}
          columns={columns}
          options={{
            ...options,
            textLabels: {
              ...options.textLabels,
              body: {
                ...options.textLabels.body,
                noMatch: loading ? (
                  <Typography variant="body2">Loading...</Typography>
                ) : (
                  'No records found'
                )
              }
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default EarningTable;